import { DOMParser, XMLSerializer } from '@xmldom/xmldom';

export const MAX_ICON_BYTES = 256 * 1024;
export const MAX_ICON_DIMENSION = 2048;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const encoder = new TextEncoder();
const fail = message => { throw new Error(message); };
const ascii = (bytes, start, length) => String.fromCharCode(...bytes.subarray(start, start + length));
const u16 = (bytes, offset) => bytes[offset] * 256 + bytes[offset + 1];
const u32 = (bytes, offset) => (bytes[offset] * 0x1000000 + bytes[offset + 1] * 0x10000 + bytes[offset + 2] * 256 + bytes[offset + 3]) >>> 0;
const le32 = (bytes, offset) => (bytes[offset] + bytes[offset + 1] * 256 + bytes[offset + 2] * 0x10000 + bytes[offset + 3] * 0x1000000) >>> 0;
const le24 = (bytes, offset) => bytes[offset] + bytes[offset + 1] * 256 + bytes[offset + 2] * 0x10000;

function dimensions(width, height) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || width > MAX_ICON_DIMENSION || height > MAX_ICON_DIMENSION) fail(`图标宽高必须大于 0 且不超过 ${MAX_ICON_DIMENSION}。`);
  return { width, height };
}

function base64(bytes) {
  let binary = '';
  for (let start = 0; start < bytes.length; start += 4096) binary += String.fromCharCode(...bytes.subarray(start, start + 4096));
  return btoa(binary);
}

function decodeDataUrl(dataUrl) {
  if (typeof dataUrl !== 'string' || dataUrl.length > MAX_ICON_BYTES * 3 + 200) fail('图标数据无效，单个图标不能超过 256 KB。');
  const match = /^data:(image\/(?:svg\+xml|png|jpeg|webp))((?:;charset=utf-8)?)(;base64)?,([\s\S]*)$/i.exec(dataUrl);
  if (!match) fail('仅支持 SVG、PNG、JPEG 和 WebP 图标。');
  const mimeType = match[1].toLowerCase();
  let bytes;
  try {
    if (match[3]) {
      const encoded = match[4];
      if (!encoded || encoded.length % 4 || !/^[A-Za-z0-9+/]*={0,2}$/.test(encoded)) throw new Error();
      const binary = atob(encoded);
      bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      if (base64(bytes) !== encoded) throw new Error();
    } else {
      if (mimeType !== 'image/svg+xml') throw new Error();
      bytes = encoder.encode(decodeURIComponent(match[4]));
    }
  } catch { fail('图标数据编码损坏，请重新选择文件。'); }
  if (!bytes.length || bytes.length > MAX_ICON_BYTES) fail('单个图标不能超过 256 KB。');
  return { mimeType, bytes };
}

const commonAttributes = new Set(['fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-dasharray', 'stroke-dashoffset', 'fill-rule', 'clip-rule', 'opacity', 'fill-opacity', 'stroke-opacity', 'transform', 'vector-effect', 'id']);
const elementAttributes = {
  svg: new Set(['xmlns', 'viewBox', 'width', 'height', 'preserveAspectRatio']), g: new Set(),
  path: new Set(['d', 'pathLength']), rect: new Set(['x', 'y', 'width', 'height', 'rx', 'ry']),
  circle: new Set(['cx', 'cy', 'r']), ellipse: new Set(['cx', 'cy', 'rx', 'ry']),
  line: new Set(['x1', 'y1', 'x2', 'y2']), polyline: new Set(['points']), polygon: new Set(['points']),
  title: new Set(), desc: new Set()
};
const scalar = /^[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[-+]?\d+)?(?:px)?$/i;
const numericList = /^[-+\d.eE,\s]+$/;
function numbers(value) {
  if (!value.trim() || !numericList.test(value)) fail('SVG 中包含无效的数值。');
  const result = value.trim().split(/[\s,]+/).map(Number);
  if (!result.every(number => Number.isFinite(number) && Math.abs(number) <= 1000000)) fail('SVG 中的数值超出允许范围。');
  return result;
}

function validateAttribute(name, value) {
  if (name === 'xmlns') { if (value !== SVG_NAMESPACE) fail('SVG 命名空间无效。'); return; }
  if (/url\s*\(|(?:https?|data|javascript|file):|@import/i.test(value)) fail('SVG 图标不能包含 URL 或外部资源。');
  if (name === 'id') { if (!/^[A-Za-z_][\w.:-]{0,63}$/.test(value)) fail('SVG 图元身份无效。'); return; }
  if (name === 'fill' || name === 'stroke') { if (!/^(?:none|currentColor|transparent|#[a-f\d]{3,8}|[a-z]{1,24})$/i.test(value)) fail('SVG 颜色仅支持纯色、none 或 currentColor。'); return; }
  if (name === 'd') { if (value.length > 65536 || !/^[Mm][MmZzLlHhVvCcSsQqTtAa\deE+.,\s-]*$/.test(value.trim())) fail('SVG 路径数据无效或过长。'); return; }
  if (name === 'points') { const list = numbers(value); if (list.length < 4 || list.length % 2 || list.length > 10000) fail('SVG 多边形坐标无效。'); return; }
  if (name === 'viewBox') { const list = numbers(value); if (list.length !== 4) fail('SVG viewBox 必须包含四个数值。'); dimensions(list[2], list[3]); return; }
  if (name === 'transform') {
    const pattern = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
    let found = false, match;
    while ((match = pattern.exec(value))) {
      found = true; const list = numbers(match[2]);
      if (!(match[1] === 'matrix' ? list.length === 6 : ['translate', 'scale'].includes(match[1]) ? [1, 2].includes(list.length) : match[1] === 'rotate' ? [1, 3].includes(list.length) : list.length === 1)) fail('SVG 变换参数无效。');
    }
    if (!found || value.replace(pattern, '').replace(/[\s,]/g, '')) fail('SVG 只支持标准数值变换。'); return;
  }
  if (name === 'stroke-dasharray') { if (value !== 'none' && numbers(value).some(number => number < 0)) fail('SVG 虚线参数无效。'); return; }
  const enums = { 'stroke-linecap': ['butt', 'round', 'square'], 'stroke-linejoin': ['miter', 'round', 'bevel'], 'fill-rule': ['nonzero', 'evenodd'], 'clip-rule': ['nonzero', 'evenodd'], 'vector-effect': ['none', 'non-scaling-stroke'] };
  if (enums[name]) { if (!enums[name].includes(value)) fail('SVG 图形属性无效。'); return; }
  if (name === 'preserveAspectRatio') { if (!/^(?:none|x(?:Min|Mid|Max)Y(?:Min|Mid|Max)(?:\s+(?:meet|slice))?)$/.test(value)) fail('SVG 比例属性无效。'); return; }
  if (!scalar.test(value) || !Number.isFinite(Number(value.replace(/px$/i, ''))) || Math.abs(Number(value.replace(/px$/i, ''))) > 1000000) fail('SVG 图形尺寸或样式数值无效。');
  if (['opacity', 'fill-opacity', 'stroke-opacity'].includes(name) && (Number(value) < 0 || Number(value) > 1)) fail('SVG 不透明度必须在 0 到 1 之间。');
}

export function sanitizeSvg(svg) {
  if (typeof svg !== 'string' || !svg.trim() || encoder.encode(svg).length > MAX_ICON_BYTES) fail('SVG 图标不能为空或超过 256 KB。');
  if (/<!\s*(?:DOCTYPE|ENTITY)\b/i.test(svg) || /<\?(?!xml\s)/i.test(svg)) fail('SVG 图标不能包含实体或处理指令。');
  const errors = [];
  let document;
  try { document = new DOMParser({ onError(level, message) { errors.push(message); } }).parseFromString(svg, 'image/svg+xml'); }
  catch { fail('SVG 格式损坏。'); }
  if (errors.length || document?.documentElement?.nodeName !== 'svg' || document.getElementsByTagName('parsererror').length) fail('SVG 格式损坏，根元素必须是 svg。');
  if (Array.from(document.childNodes).filter(node => node.nodeType === 1).length !== 1) fail('SVG 只能包含一个根元素。');
  const root = document.documentElement;
  let count = 0, shapes = 0;
  function inspect(element, depth) {
    if (++count > 512 || depth > 24) fail('SVG 图元过多或层级过深。');
    const allowed = elementAttributes[element.nodeName];
    if (!allowed || (element.namespaceURI && element.namespaceURI !== SVG_NAMESPACE)) fail('SVG 仅允许基础向量图形，不能包含脚本、图片、引用或嵌入内容。');
    if (!['svg', 'g', 'title', 'desc'].includes(element.nodeName)) shapes++;
    if (element.nodeName === 'svg' && element !== root) fail('SVG 不支持嵌套根元素。');
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name;
      if (/^on/i.test(name) || name === 'style' || name.includes(':') || (!commonAttributes.has(name) && !allowed.has(name))) fail('SVG 包含不允许的属性；请去掉样式、事件与外部引用。');
      validateAttribute(name, attribute.value);
    }
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === 1) {
        if (['title', 'desc'].includes(element.nodeName)) fail('SVG 标题和描述只能包含纯文字。');
        inspect(child, depth + 1);
      } else if ([3, 4].includes(child.nodeType)) {
        if (!['title', 'desc'].includes(element.nodeName) && child.nodeValue.trim()) fail('SVG 基础图形中不能包含文字节点。');
        if (child.nodeValue.length > 1000) fail('SVG 文字说明过长。');
      } else if (child.nodeType === 8) element.removeChild(child);
      else fail('SVG 包含不允许的内容。');
    }
  }
  inspect(root, 0);
  if (!shapes) fail('SVG 至少需要一个基础向量图形。');
  let width, height;
  if (root.hasAttribute('viewBox')) { const box = numbers(root.getAttribute('viewBox')); width = box[2]; height = box[3]; }
  else {
    width = Number(root.getAttribute('width').replace(/px$/i, '')); height = Number(root.getAttribute('height').replace(/px$/i, ''));
    dimensions(width, height); root.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  if (root.hasAttribute('width')) dimensions(Number(root.getAttribute('width').replace(/px$/i, '')), height);
  if (root.hasAttribute('height')) dimensions(width, Number(root.getAttribute('height').replace(/px$/i, '')));
  root.setAttribute('xmlns', SVG_NAMESPACE);
  const clean = new XMLSerializer().serializeToString(root);
  if (encoder.encode(clean).length > MAX_ICON_BYTES) fail('规范化后的 SVG 超过 256 KB。');
  return { svg: clean, ...dimensions(width, height) };
}

const crcTable = Array.from({ length: 256 }, (_, value) => {
  for (let bit = 0; bit < 8; bit++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
  return value >>> 0;
});
function crc32(bytes) { let crc = 0xffffffff; for (const byte of bytes) crc = crcTable[(crc ^ byte) & 255] ^ (crc >>> 8); return (crc ^ 0xffffffff) >>> 0; }
function pngSize(bytes) {
  if (bytes.length < 45 || [137, 80, 78, 71, 13, 10, 26, 10].some((byte, index) => bytes[index] !== byte)) fail('PNG 文件签名无效。');
  let offset = 8, width, height, imageData = false, ended = false;
  while (offset < bytes.length) {
    if (offset + 12 > bytes.length) fail('PNG 图块数据损坏。');
    const length = u32(bytes, offset), type = ascii(bytes, offset + 4, 4), end = offset + length + 12;
    if (end > bytes.length || !/^[A-Za-z]{4}$/.test(type) || crc32(bytes.subarray(offset + 4, end - 4)) !== u32(bytes, end - 4)) fail('PNG 图块校验失败。');
    if (offset === 8 && (type !== 'IHDR' || length !== 13)) fail('PNG 缺少有效头部。');
    if (type === 'IHDR') {
      if (offset !== 8 || length !== 13) fail('PNG 头部重复或损坏。');
      width = u32(bytes, offset + 8); height = u32(bytes, offset + 12);
      if (![1, 2, 4, 8, 16].includes(bytes[offset + 16]) || ![0, 2, 3, 4, 6].includes(bytes[offset + 17]) || bytes[offset + 18] !== 0 || bytes[offset + 19] !== 0 || bytes[offset + 20] > 1) fail('PNG 编码参数无效。');
      dimensions(width, height);
    }
    if (['acTL', 'fcTL', 'fdAT'].includes(type)) fail('图标暂不支持动画图片。');
    if (type === 'IDAT') imageData = true;
    if (type === 'IEND') { if (length || end !== bytes.length) fail('PNG 结束标记无效。'); ended = true; }
    offset = end;
  }
  if (!ended || !imageData) fail('PNG 缺少图像数据或结束标记。');
  return dimensions(width, height);
}
function jpegSize(bytes) {
  if (bytes.length < 20 || bytes[0] !== 255 || bytes[1] !== 216 || bytes.at(-2) !== 255 || bytes.at(-1) !== 217) fail('JPEG 文件签名或结束标记无效。');
  let offset = 2, width, height, scan = false;
  while (offset < bytes.length - 2) {
    if (bytes[offset++] !== 255) fail('JPEG 图像头部损坏。');
    while (bytes[offset] === 255) offset++;
    const marker = bytes[offset++];
    if (marker === 218) { scan = true; break; }
    if ([216, 217, 0].includes(marker)) fail('JPEG 图像标记无效。');
    if (marker === 1 || marker >= 208 && marker <= 215) continue;
    if (offset + 2 > bytes.length) fail('JPEG 图像头部损坏。');
    const length = u16(bytes, offset);
    if (length < 2 || offset + length > bytes.length) fail('JPEG 图像头部长度无效。');
    if ([192, 193, 194, 195, 197, 198, 199, 201, 202, 203, 205, 206, 207].includes(marker)) {
      if (length < 8 || bytes[offset + 2] !== 8) fail('JPEG 图像编码不受支持。');
      height = u16(bytes, offset + 3); width = u16(bytes, offset + 5); dimensions(width, height);
    }
    offset += length;
  }
  if (!scan || !width || !height) fail('JPEG 缺少有效的图像帧。');
  return dimensions(width, height);
}
function webpSize(bytes) {
  if (bytes.length < 20 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP' || le32(bytes, 4) + 8 !== bytes.length) fail('WebP 文件签名或长度无效。');
  let offset = 12, frame, canvas;
  while (offset < bytes.length) {
    if (offset + 8 > bytes.length) fail('WebP 图像块损坏。');
    const type = ascii(bytes, offset, 4), size = le32(bytes, offset + 4), start = offset + 8, end = start + size;
    if (end + (size % 2) > bytes.length) fail('WebP 图像块长度无效。');
    if (['ANIM', 'ANMF'].includes(type)) fail('图标暂不支持动画图片。');
    if (type === 'VP8X') {
      if (size !== 10 || bytes[start] & 2) fail('WebP 扩展头部无效或包含动画。');
      canvas = dimensions(le24(bytes, start + 4) + 1, le24(bytes, start + 7) + 1);
    } else if (type === 'VP8 ') {
      if (frame || size < 10 || bytes[start] & 1 || ascii(bytes, start + 3, 3) !== '\x9d\x01\x2a') fail('WebP 有损图像帧无效。');
      frame = dimensions((bytes[start + 6] | bytes[start + 7] << 8) & 0x3fff, (bytes[start + 8] | bytes[start + 9] << 8) & 0x3fff);
    } else if (type === 'VP8L') {
      if (frame || size < 5 || bytes[start] !== 47) fail('WebP 无损图像帧无效。');
      const packed = le32(bytes, start + 1); frame = dimensions((packed & 0x3fff) + 1, (packed >>> 14 & 0x3fff) + 1);
    }
    offset = end + size % 2;
  }
  if (!frame || canvas && (canvas.width !== frame.width || canvas.height !== frame.height)) fail('WebP 图像尺寸无效。');
  return frame;
}

// Synchronous and environment-independent: Graph validation, browser previews,
// server upload handling and standalone exports share exactly this boundary.
const cleanIconCache = new Map();
export function sanitizeIcon(input) {
  const key = input?.dataUrl;
  if (typeof key === 'string' && cleanIconCache.has(key)) {
    const cached = cleanIconCache.get(key); cleanIconCache.delete(key); cleanIconCache.set(key, cached); return { ...cached };
  }
  const { mimeType, bytes } = decodeDataUrl(input?.dataUrl);
  let size, clean = bytes;
  if (mimeType === 'image/svg+xml') {
    let text;
    try { text = new TextDecoder('utf-8', { fatal: true }).decode(bytes); } catch { fail('SVG 必须使用有效的 UTF-8 编码。'); }
    const result = sanitizeSvg(text); size = { width: result.width, height: result.height }; clean = encoder.encode(result.svg);
  } else size = mimeType === 'image/png' ? pngSize(bytes) : mimeType === 'image/jpeg' ? jpegSize(bytes) : webpSize(bytes);
  const result = { dataUrl: `data:${mimeType};base64,${base64(clean)}`, mimeType, ...size };
  // Drag previews validate the same immutable resource repeatedly. Bound the
  // cache while avoiding XML parsing and image decoding on each pointer move.
  for (const dataUrl of [key, result.dataUrl]) { cleanIconCache.delete(dataUrl); cleanIconCache.set(dataUrl, result); }
  while (cleanIconCache.size > 32) cleanIconCache.delete(cleanIconCache.keys().next().value);
  return { ...result };
}
